/**
 * Разбор банковской выписки в формате обмена с 1С (`1CClientBankExchange`) —
 * раздел «Банк» (этап 3, Б2). Чистые функции: одни правила на сайте (предпросмотр)
 * и на сервере (запись). Образцы и разбор — docs/customer-meetings.md, 08.10.2026.
 *
 * Файл приходит в Windows-1251 (реже DOS) — перекодирует вызывающая сторона,
 * сюда попадает уже текст.
 */
import type { BankLineKind } from '../constants/bank'

export interface BankInvoiceRef {
  /** Номер счёта, как в назначении платежа («1298»). */
  number: string
  /** Дата счёта `YYYY-MM-DD`, если указана («от 25.09.2026», «от 16 июля 2026г.»). */
  date: string | null
}

export interface ParsedBankLine {
  docType: string
  docNumber: string
  /** Дата документа `YYYY-MM-DD`. */
  docDate: string | null
  /** Дата поступления на счёт или списания со счёта — ею датируется оплата. */
  postedAt: string | null
  direction: 'IN' | 'OUT'
  amount: number
  payerName: string
  payerInn: string
  payerAccount: string
  receiverName: string
  receiverInn: string
  receiverAccount: string
  purpose: string
  kind: BankLineKind
  /** Счета, названные в назначении платежа (пусто — «по договору», счёт-фактура и т. п.). */
  invoiceRefs: BankInvoiceRef[]
  /** Ключ строки: повторная загрузка той же выписки строку не задваивает. */
  dedupeKey: string
}

export interface ParsedBankStatement {
  accountNumber: string
  periodFrom: string | null
  periodTo: string | null
  openingBalance: number | null
  closingBalance: number | null
  totalIn: number | null
  totalOut: number | null
  /** Владелец счёта — по первой строке, где наш счёт стоит у плательщика или получателя. */
  ownerName: string
  ownerInn: string
  bankName: string
  bik: string
  lines: ParsedBankLine[]
}

export class BankStatementFormatError extends Error {}

const HEADER = '1CClientBankExchange'

/** «28.09.2026» / «28.09.26» → `2026-09-28`; мусор → null. */
export function parseBankDate(raw: string | undefined): string | null {
  const m = raw?.trim().match(/^(\d{1,2})[./](\d{1,2})[./](\d{2}|\d{4})$/)
  if (!m) return null
  const day = Number(m[1])
  const month = Number(m[2])
  const yearRaw = m[3] ?? ''
  const year = yearRaw.length === 2 ? 2000 + Number(yearRaw) : Number(yearRaw)
  if (month < 1 || month > 12 || day < 1 || day > 31) return null
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

function parseAmount(raw: string | undefined): number | null {
  if (raw == null) return null
  const cleaned = raw.replace(/[\s\xA0]/g, '').replace(',', '.')
  if (!/^-?\d+(\.\d+)?$/.test(cleaned)) return null
  return Math.round(Number(cleaned) * 100) / 100
}

const MONTHS_RU: Record<string, number> = {
  января: 1,
  февраля: 2,
  марта: 3,
  апреля: 4,
  мая: 5,
  июня: 6,
  июля: 7,
  августа: 8,
  сентября: 9,
  октября: 10,
  ноября: 11,
  декабря: 12,
}

/** Дата после номера счёта: «от 25.09.2026», «от 24.09.26 г.», «от 16 июля 2026г.». */
function parseInvoiceDate(tail: string): string | null {
  const numeric = tail.match(/^\s*от\s*(\d{1,2}[./]\d{1,2}[./](?:\d{4}|\d{2}))(?!\d)/i)
  if (numeric) return parseBankDate(numeric[1])
  const worded = tail.match(/^\s*от\s*(\d{1,2})\s+([а-яё]+)\s+(\d{4})/i)
  if (!worded) return null
  const month = MONTHS_RU[(worded[2] ?? '').toLowerCase()]
  if (!month) return null
  return parseBankDate(`${worded[1]}.${month}.${worded[3]}`)
}

/**
 * Номера счетов из назначения платежа — только после слова «счёт» / «сч.»:
 * рядом стоят номера договоров и писем («договор № 1443/ЭКБ»). Счёт-фактура
 * («СЧФ 1084», «счет-фактура № 5», «с/ф») — другая нумерация 1С, её не берём:
 * такая строка уйдёт на ручной выбор. Номер длиннее 10 цифр — не наш счёт.
 */
export function parseInvoiceRefs(purpose: string): BankInvoiceRef[] {
  const refs: BankInvoiceRef[] = []
  const seen = new Set<string>()
  // «сч» не внутри слова; дальше «ёт/ету/ета…» или точка; затем необязательные
  // «на оплату» и знак номера; затем сам номер.
  const re =
    /(?<![а-яёa-z])сч(?:[её]т[а-яё]*|\.)?\s*(?:на\s+оплату\s*)?(?:№|N|#)?\s*(\d{1,10})(?![\d/])/gi
  for (const m of purpose.matchAll(re)) {
    const number = (m[1] ?? '').replace(/^0+(?=\d)/, '')
    if (seen.has(number)) continue
    seen.add(number)
    const tail = purpose.slice((m.index ?? 0) + m[0].length, (m.index ?? 0) + m[0].length + 40)
    refs.push({ number, date: parseInvoiceDate(tail) })
  }
  return refs
}

/** Депозит: проценты и возврат вклада — не оплата клиента. */
const DEPOSIT_RE = /депозит/i
/** Возврат денег (от поставщика, ошибочный платёж, зарплатный и т. п.). */
const REFUND_RE = /(?<![а-яё])возврат/i

/** Назначение начинается со слова «возврат» — это возврат, даже если назван счёт. */
const REFUND_LEAD_RE = /^\s*возврат/i

export function classifyBankLine(direction: 'IN' | 'OUT', purpose: string): BankLineKind {
  if (direction === 'OUT') return 'OUTGOING'
  if (DEPOSIT_RE.test(purpose)) return 'DEPOSIT'
  // «Оплата по сч. 1298 … с учётом возврата брака» — оплата клиента, не возврат
  // (аудит 20.10.2026): «возврат» в середине при названном счёте не считаем.
  if (
    REFUND_RE.test(purpose) &&
    (REFUND_LEAD_RE.test(purpose) || parseInvoiceRefs(purpose).length === 0)
  ) {
    return 'REFUND'
  }
  return 'INCOMING'
}

type Fields = Record<string, string>

function buildLine(docType: string, f: Fields, accountNumber: string): ParsedBankLine | null {
  const amount = parseAmount(f['Сумма'])
  // Нулевая или отрицательная сумма — не движение денег (аудит 20.10.2026): раньше
  // такая строка вставала поступлением с минусом и висела в «Не разнесено».
  if (amount == null || amount <= 0) return null
  const payerAccount = f['ПлательщикСчет'] ?? ''
  const receiverAccount = f['ПолучательСчет'] ?? ''
  // Поступление — наш счёт у получателя; если счёт не совпал ни там, ни там
  // (банк подставил транзитный), смотрим, какая дата проведения заполнена.
  const direction: 'IN' | 'OUT' =
    receiverAccount === accountNumber
      ? 'IN'
      : payerAccount === accountNumber
        ? 'OUT'
        : f['ДатаПоступило']
          ? 'IN'
          : 'OUT'
  const purpose = (f['НазначениеПлатежа'] ?? '').trim()
  const docNumber = f['Номер'] ?? ''
  const docDate = parseBankDate(f['Дата'])
  const postedAt = parseBankDate(direction === 'IN' ? f['ДатаПоступило'] : f['ДатаСписано'])
  const kind = classifyBankLine(direction, purpose)
  const counterAccount = direction === 'IN' ? payerAccount : receiverAccount
  return {
    docType,
    docNumber,
    docDate,
    postedAt: postedAt ?? docDate,
    direction,
    amount,
    payerName: f['Плательщик1'] ?? f['Плательщик'] ?? '',
    payerInn: f['ПлательщикИНН'] ?? '',
    payerAccount,
    receiverName: f['Получатель1'] ?? f['Получатель'] ?? '',
    receiverInn: f['ПолучательИНН'] ?? '',
    receiverAccount,
    purpose,
    kind,
    invoiceRefs: kind === 'INCOMING' ? parseInvoiceRefs(purpose) : [],
    dedupeKey: [
      accountNumber,
      direction,
      docNumber,
      docDate ?? '',
      amount.toFixed(2),
      counterAccount,
    ].join('|'),
  }
}

/**
 * Разбирает текст выписки. Бросает `BankStatementFormatError` с русским текстом,
 * если это не выписка в формате обмена с 1С или в ней нет расчётного счёта.
 */
export function parseBankStatement(text: string): ParsedBankStatement {
  // Файл в UTF-8 может начинаться с метки порядка байтов.
  const rows = (text.charCodeAt(0) === 0xfeff ? text.slice(1) : text)
    .split(/\r?\n/)
    .map((r) => r.trim())
    .filter((r) => r !== '')
  if (rows[0] !== HEADER) {
    throw new BankStatementFormatError(
      'Это не банковская выписка в формате обмена с 1С: первая строка файла должна быть «1CClientBankExchange»',
    )
  }

  const head: Fields = {}
  const account: Fields = {}
  // Все наши счета файла — из шапки и из каждой секции «СекцияРасчСчет».
  const accounts = new Set<string>()
  const docs: Array<{ type: string; fields: Fields }> = []
  let section: 'head' | 'account' | 'doc' = 'head'
  let current: { type: string; fields: Fields } | null = null

  for (const row of rows.slice(1)) {
    if (row === 'СекцияРасчСчет') {
      section = 'account'
      continue
    }
    if (row === 'КонецРасчСчет' || row === 'КонецДокумента') {
      if (current) docs.push(current)
      current = null
      section = 'head'
      continue
    }
    if (row === 'КонецФайла') break
    const eq = row.indexOf('=')
    if (eq < 0) continue
    const key = row.slice(0, eq).trim()
    const value = row.slice(eq + 1).trim()
    if (key === 'СекцияДокумент') {
      section = 'doc'
      current = { type: value, fields: {} }
      continue
    }
    if (section !== 'doc' && key === 'РасчСчет' && value) accounts.add(value)
    if (section === 'doc' && current) current.fields[key] = value
    else if (section === 'account') account[key] ??= value
    else head[key] ??= value
  }

  const accountNumber = account['РасчСчет'] ?? head['РасчСчет'] ?? ''
  if (!/^\d{20}$/.test(accountNumber)) {
    throw new BankStatementFormatError(
      'В выписке не найден расчётный счёт (строка «РасчСчет=» из 20 цифр)',
    )
  }
  // Выписка по нескольким счетам: все платежи записались бы на первый счёт, авторазнос
  // искал бы счета не того юрлица, а отдельная выписка второго счёта задвоила бы строки
  // (аудит 20.10.2026). Просим файл по каждому счёту.
  if (accounts.size > 1) {
    throw new BankStatementFormatError(
      `В файле выписка сразу по ${accounts.size} расчётным счетам (${[...accounts].join(', ')}). Выгрузите из банка отдельный файл по каждому счёту`,
    )
  }

  const lines: ParsedBankLine[] = []
  let ownerName = ''
  let ownerInn = ''
  let bankName = ''
  let bik = ''
  for (const doc of docs) {
    const line = buildLine(doc.type, doc.fields, accountNumber)
    if (!line) continue
    lines.push(line)
    if (ownerInn) continue
    const side =
      line.receiverAccount === accountNumber
        ? 'Получатель'
        : line.payerAccount === accountNumber
          ? 'Плательщик'
          : null
    if (!side) continue
    ownerName = doc.fields[`${side}1`] ?? ''
    ownerInn = doc.fields[`${side}ИНН`] ?? ''
    bankName = doc.fields[`${side}Банк1`] ?? ''
    bik = doc.fields[`${side}БИК`] ?? ''
  }

  return {
    accountNumber,
    periodFrom: parseBankDate(account['ДатаНачала'] ?? head['ДатаНачала']),
    periodTo: parseBankDate(account['ДатаКонца'] ?? head['ДатаКонца']),
    openingBalance: parseAmount(account['НачальныйОстаток']),
    closingBalance: parseAmount(account['КонечныйОстаток']),
    totalIn: parseAmount(account['ВсегоПоступило']),
    totalOut: parseAmount(account['ВсегоСписано']),
    ownerName,
    ownerInn,
    bankName,
    bik,
    lines,
  }
}
